// ============================================================================
// File: EntityGrowthPanel.tsx — Spec 160 T019
// Author: Peter Dobler | Created: 2026-05-31
// Feature: 160 - Dashboard Redesign (US1–US4)
// Constitution: v1.15.0 | Principle XXXVII
// ============================================================================
import React from 'react';
import {
  LineChart, Line, ResponsiveContainer, Tooltip, XAxis,
} from 'recharts';
import { DashboardPanel, Spinner, chartTooltipStyle, AppTooltip, ErrorState } from '@/components/ui';
import type { EntityGrowth } from '../../../../../shared/types/dashboard';

interface EntityGrowthPanelProps {
  label: string;
  icon?: React.ReactNode;
  data: EntityGrowth | null | undefined;
  loading?: boolean;
  color?: string;
  /** Hover explanation of the tile (defaults to a label-derived description). */
  tooltip?: React.ReactNode;
}

/** Default hover text per entity tile (used when no explicit tooltip is passed). */
const GROWTH_TIP: Record<string, string> = {
  Workspaces: 'Fabric workspaces under management. The line is the 60-day count trend.',
  Lakehouses: 'Fabric lakehouses under management. The line is the 60-day count trend.',
  Warehouses: 'Fabric warehouses under management. The line is the 60-day count trend.',
  'Container Apps': 'Azure Container Apps under management. The line is the 60-day count trend.',
};

export const EntityGrowthPanel: React.FC<EntityGrowthPanelProps> = ({
  label,
  icon,
  data,
  loading = false,
  color = '#6366f1',
  tooltip,
}) => {
  const tip = tooltip ?? GROWTH_TIP[label];
  const chartData = (data?.trend60d ?? []).map(d => ({
    date: d.date,
    count: d.count,
  }));

  return (
    <DashboardPanel
      title={
        <span className="flex items-center gap-2">
          {icon}
          {tip != null ? (
            <AppTooltip title={tip} placement="top">
              <span className="cursor-help">{label}</span>
            </AppTooltip>
          ) : (
            <span>{label}</span>
          )}
        </span>
      }
    >
      {loading ? (
        <div className="flex min-h-[60px] items-center justify-center"><Spinner size="md" /></div>
      ) : !data ? (
        // Spec 298 FR-005/FR-006: a section that was not read is said so, never shown as zeros
        <ErrorState title={`Could not read the ${label} count`} />
      ) : (
        <div className="flex h-full flex-col">
          <div className="mb-2 text-2xl font-bold text-[var(--color-text-primary)]">
            {(data?.current ?? 0).toLocaleString()}
          </div>

          <div className="min-h-[48px] flex-1">
            {chartData.length > 1 ? (
              <ResponsiveContainer width="100%" height="100%">
                <LineChart data={chartData}>
                  <XAxis dataKey="date" hide />
                  <Tooltip
                    {...chartTooltipStyle}
                    formatter={((val: unknown) => [Number(val).toLocaleString(), label]) as any}
                    labelFormatter={(l: unknown) => String(l)}
                  />
                  <Line
                    type="monotone"
                    dataKey="count"
                    stroke={color}
                    dot={false}
                    strokeWidth={2}
                  />
                </LineChart>
              </ResponsiveContainer>
            ) : (
              <span className="text-xs text-[var(--color-text-secondary)]">No trend data</span>
            )}
          </div>
        </div>
      )}
    </DashboardPanel>
  );
};

export default EntityGrowthPanel;
